// ===== File: src/pages/Auth/ForgotPassword.tsx =====
import React, { useEffect, useState } from "react";
import { Button, Input, Alert } from "antd";
import AuthWrapper from "../../../../Components/AuthWrapper/AuthWrapper";
import Loader from "../../../../Components/Loader/Loader";
import { useNavigate } from "react-router-dom";
import { resetPassword } from "../../../../Services/AuthenticationService/ForgotpasswordApi";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { getClientConfig } from "../../../../config/clientConfig";
import logoImg from "../../../../Assets/Icons/IY VAULT icon-ios.png";
import testIMG from "iyvault_format1.png";
import {
  ResetApi,
  TwoFactorApi,
} from "../../../../Services/AuthenticationService/ResetApi";
import AlertMessage from "../../../../Components/AlertMessage/AlertMessage";
import { FaArrowLeft } from "react-icons/fa";
import { emailSchema, resetSchema } from "./MfaValidationSchema";

type Step = "EMAIL" | "OTP" | "RESET";

const ForgotPassword: React.FC = () => {
  const clientConfig = getClientConfig();
  const [step, setStep] = useState<Step>("EMAIL");
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [otpExpired, setOtpExpired] = useState(false);
  const [alert, setAlert] = useState<{
    type: "error" | "success";
    text: string;
  } | null>(null);
  const [userid, setuserid] = useState("");
  const navigate = useNavigate();

  const [message, setMessage] = useState("");

  const maskEmail = (email: string) => {
    const [name, domain] = email.split("@");
    return name.slice(0, 3) + "****@" + domain;
  };
  const [timer, setTimer] = useState(300);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);

  useEffect(() => {
    if (step === "OTP" && timer > 0) {
      const countdown = setTimeout(() => setTimer((prev) => prev - 1), 1000);
      return () => clearTimeout(countdown);
    }

    if (step === "OTP" && timer === 0) {
      setOtpExpired(true);
    }
  }, [step, timer]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Step 1: Email Validation Schema

  // Step 1: Verify Email
  const onVerifyEmail = async (values: { email: string }) => {
    try {
      const result = await ResetApi(values.email); //  use values.email
      AlertMessage({ type: "success", text: "Reset link sent successfully" });
      setuserid(result.data.userId);
      setEmail(values.email); //  store email for OTP step
      setStep("OTP");
    } catch (error: any) {
      AlertMessage({
        type: "error",
        text: error.message || "Something went wrong",
      });
    }
  };

  // Corrected OTP verification
  const onVerifyOtp = async (values: { otp: string }) => {
    try {
      const result = await TwoFactorApi(userid, values.otp);
      AlertMessage({
        type: "success",
        text: "Two-factor authentication successful!",
      });
      console.log("2FA result", result);
      setStep("RESET"); // move to reset step
    } catch (error: any) {
      AlertMessage({
        type: "error",
        text: error.message || "Two-factor authentication failed",
      });
    }
  };

  // UPDATED resetpassword function
  const resetpassword = async (values: {
    password: string;
    confirmPassword: string;
  }) => {
    setLoading(true);
    setAlert(null);

    try {
      await resetPassword({
        userId: userid,
        password: values.password,
      });

      AlertMessage({
        text: "Password reset successfully",
        type: "success",
      });
      navigate("/login");
      setLoading(false);
    } catch (err: any) {
      setAlert({ type: "error", text: err.message || "Something went wrong" });
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {};

  const onResetPassword = async (values: {
    password: string;
    confirmPassword: string;
  }) => {
    setAlert(null);
    try {
      const result = await ResetApi(email);
      setMessage(result.message || "Reset link sent successfully!");
    } catch (error: any) {
      setMessage(error || "Something went wrong");
    }
  };

  return (
    <AuthWrapper>
      {loading ? (
        <Loader />
      ) : (
        <div
          className="forgot-wrapper"
          style={{
            width: "100%",
            maxWidth: 400,
            minHeight: 400,
            background: "#fff",
            padding: 24,
            borderRadius: 12,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            position: "relative", // needed for arrow
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 50,
              left: 20,
              cursor: "pointer",
              fontSize: 25,
              color: "#464646ff",
              zIndex: 10,
            }}
            onClick={() => {
              if (step === "EMAIL") {
                navigate("/login"); // back to login page
              } else if (step === "OTP") {
                setStep("EMAIL"); // go back to email input
              } else if (step === "RESET") {
                setStep("OTP"); // go back to OTP page
              }
            }}
          >
            <FaArrowLeft />
          </div>

          <div style={{ textAlign: "center", marginBottom: 0 }}>
            <img
              src={clientConfig.logo_format_1 || logoImg}
              alt="Logo"
              style={{ width: 90, height: 90 }}
            />
          </div>
          <h2
            style={{
              marginBottom: 35,
              textAlign: "center",
              color: "#434344ff",
              fontSize: "var(--font-size-3xl)",
            }}
          >
            {step === "EMAIL" &&
              "Enter the email which belongs to your or something"}
            {step === "OTP" && "Verify OTP"}
            {step === "RESET" && "Reset Password"}
          </h2>

          {alert && (
            <Alert
              message={alert.text}
              type={alert.type}
              closable
              style={{ marginBottom: 15 }}
            />
          )}

          {step === "EMAIL" && (
            <Formik
              initialValues={{ email: "" }}
              validationSchema={emailSchema}
              onSubmit={onVerifyEmail}
            >
              {({ isSubmitting }) => (
                <Form>
                  <div style={{ marginBottom: 15, marginRight: 20 }}>
                    <Field
                      name="email"
                      type="email"
                      placeholder="name@iykons.com"
                      style={{
                        width: "100%",
                        height: 30,
                        padding: 8,
                        borderRadius: 4,
                        border: "1px solid #d9d9d9",
                      }}
                    />
                    <div style={{ color: "red", marginTop: 5 }}>
                      <ErrorMessage name="email" />
                    </div>
                  </div>

                  <div
                    style={{
                      textAlign: "center",
                      marginBottom: 10,
                      marginTop: 40,
                    }}
                  >
                    <div style={{ display: "flex", gap: 10, marginTop: 40 }}>
                      <Button
                        type="default"
                        htmlType="button"
                        style={{ flex: 1, height: 40 }}
                        onClick={() => navigate("/login")}
                      >
                        Cancel
                      </Button>

                      <Button
                        type="primary"
                        htmlType="submit"
                        style={{
                          flex: 1,
                          height: 40,
                          backgroundColor: "var(--color-brand)",
                        }}
                        disabled={isSubmitting}
                      >
                        Verify
                      </Button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          )}

          {step === "OTP" && (
            <Formik
              initialValues={{ otp: "" }}
              validationSchema={Yup.object({
                otp: Yup.string()
                  .required("OTP is required")
                  .matches(/^\d{6}$/, "OTP must be a 6-digit number"),
              })}
              onSubmit={onVerifyOtp}
            >
              {({ handleSubmit, errors, touched }) => (
                <Form>
                  <div
                    style={{
                      marginBottom: 10,
                      textAlign: "center",
                      fontWeight: "bold",
                      color: otpExpired ? "red" : "#555",
                    }}
                  >
                    {otpExpired
                      ? "Your OTP has expired"
                      : `Your OTP expires in ${formatTime(timer)}`}
                  </div>

                  <Field
                    name="otp"
                    placeholder="Enter OTP"
                    style={{
                      width: "100%",
                      height: 30,
                      padding: 8,
                      borderRadius: 4,
                      border: "1px solid #d9d9d9,paddingLeft:20px",
                    }}
                  />
                  {errors.otp && touched.otp && (
                    <div style={{ color: "red", marginTop: 5 }}>
                      {errors.otp}
                    </div>
                  )}

                  <div style={{ display: "flex", gap: 10, marginTop: 40 }}>
                    <Button
                      type="default"
                      htmlType="button"
                      style={{ flex: 1, height: 40 }}
                      onClick={() => setStep("EMAIL")}
                    >
                      Cancel
                    </Button>

                    <Button
                      type="primary"
                      onClick={() => handleSubmit()}
                      style={{
                        flex: 1,
                        height: 40,
                        backgroundColor: "var(--color-brand)",
                      }}
                    >
                      Verify OTP
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          )}

          {step === "RESET" && (
            <Formik
              initialValues={{ password: "", confirmPassword: "" }}
              validationSchema={resetSchema}
              onSubmit={resetpassword}
            >
              {({ isSubmitting }) => (
                <Form>
                  <div style={{ marginBottom: 15, marginRight: 20 }}>
                    <Field
                      as={Input.Password}
                      placeholder="Enter new password"
                      name="password"
                      style={{
                        width: "100%",
                        height: 50,
                        padding: 8,
                        borderRadius: 4,
                        border: "1px solid #d9d9d9",
                      }}
                    />
                    <div style={{ color: "red", marginTop: 5 }}>
                      <ErrorMessage name="password" />
                    </div>
                  </div>

                  <div style={{ marginBottom: 15, marginRight: 20 }}>
                    <Field
                      as={Input.Password}
                      placeholder="Confirm your password"
                      name="confirmPassword"
                      style={{
                        width: "100%",
                        height: 50,
                        padding: 8,
                        borderRadius: 4,
                        border: "1px solid #d9d9d9",
                      }}
                    />
                    <div style={{ color: "red", marginTop: 5 }}>
                      <ErrorMessage name="confirmPassword" />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 3, marginTop: 40 }}>
                    <Button
                      type="default"
                      htmlType="button"
                      style={{ flex: 1, height: 40 }}
                      onClick={() => setStep("OTP")}
                    >
                      Cancel
                    </Button>

                    <Button
                      type="primary"
                      htmlType="submit"
                      style={{
                        flex: 1,
                        height: 40,
                        backgroundColor: "var(--color-brand)",
                      }}
                      disabled={isSubmitting}
                    >
                      Reset Password
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </div>
      )}
    </AuthWrapper>
  );
};

export default ForgotPassword;
